import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Shield, Mail, MapPin, Camera, Bell,
  Phone, Eye, Trash2, Lock, Globe, ExternalLink,
} from "lucide-react";

import "../styles/landing-luxury.css";
import "../styles/phase2-public.css";

import LuxButton from "../components/landing/LuxButton";
import LuxPill from "../components/landing/LuxPill";
import LandingFooter from "../components/landing/LandingFooter";

const SECTIONS = [
  { id: "data-collected", n: "1",  title: "Information We Collect" },
  { id: "data-safety",    n: "2",  title: "Data Safety Summary" },
  { id: "permissions",    n: "3",  title: "Device Permissions" },
  { id: "usage",          n: "4",  title: "How We Use Information" },
  { id: "third-parties",  n: "5",  title: "Third-Party Services" },
  { id: "security",       n: "6",  title: "Data Security" },
  { id: "retention",      n: "7",  title: "Data Retention & Deletion" },
  { id: "rights",         n: "8",  title: "Your Privacy Rights" },
  { id: "transfers",      n: "9",  title: "International Transfers" },
  { id: "children",       n: "10", title: "Children's Privacy" },
  { id: "cookies",        n: "11", title: "Cookies & Tracking" },
  { id: "changes",        n: "12", title: "Changes to This Policy" },
  { id: "contact",        n: "13", title: "Contact Us" },
];

const useActiveSection = (ids) => {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); }); },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 },
    );
    ids.forEach((id) => { const el = document.getElementById(id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [ids]);
  return active;
};

// Data table row used in §2
const DataRow = ({ type, collected, purpose, shared }) => (
  <tr>
    <td>{type}</td>
    <td>{collected}</td>
    <td>{purpose}</td>
    <td>{shared}</td>
  </tr>
);

const ThirdPartyService = ({ name, purpose, data, url }) => (
  <div
    className="lux-card lux-card-glass"
    style={{ padding: 16 }}
  >
    <div className="flex items-center justify-between mb-1.5">
      <p className="text-[14px] font-semibold text-[var(--lux-text)]">{name}</p>
      <a
        href={url} target="_blank" rel="noopener noreferrer"
        className="text-[12px] flex items-center gap-1"
        style={{ color: "var(--lux-gold)" }}
      >
        Privacy Policy <ExternalLink className="w-3 h-3" />
      </a>
    </div>
    <p className="text-[12px]" style={{ color: "var(--lux-text-2)" }}>{purpose}</p>
    <p className="text-[11px] mt-1" style={{ color: "var(--lux-text-3)" }}>Data shared: {data}</p>
  </div>
);

const PermRow = ({ icon: Icon, title, body }) => (
  <div
    className="flex items-start gap-3 rounded-xl p-4"
    style={{ background: "rgba(255, 255, 255, 0.025)", border: "1px solid var(--lux-border)" }}
  >
    <Icon className="w-5 h-5 mt-0.5 shrink-0" style={{ color: "var(--lux-gold)" }} />
    <div>
      <p className="text-[14px] font-medium text-[var(--lux-text)] mb-1">{title}</p>
      <p className="text-[14px]" style={{ color: "var(--lux-text-2)" }}>{body}</p>
    </div>
  </div>
);

const PrivacyPolicyPage = () => {
  const lastUpdated = "February 28, 2026";
  const effectiveDate = "February 28, 2026";
  const active = useActiveSection(SECTIONS.map((s) => s.id));

  return (
    <div className="lux">
      <div className="lux-prose-shell">
        {/* Mini-header */}
        <header className="lux-mini-header">
          <div className="max-w-[1200px] mx-auto px-5 lg:px-8 flex items-center justify-between h-16">
            <Link to="/" className="flex items-center"><img src="/logo.png" alt="Fieldz AI" className="h-9 object-contain" /></Link>
            <Link to="/">
              <LuxButton variant="quiet"><ArrowLeft className="w-4 h-4" /> Back</LuxButton>
            </Link>
          </div>
        </header>

        {/* Hero */}
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8 pt-12 pb-8 relative z-10">
          <div className="flex items-start gap-4 max-w-[800px]">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
              style={{ background: "rgba(201, 168, 76, 0.10)", border: "1px solid rgba(201, 168, 76, 0.25)" }}
            >
              <Shield className="w-6 h-6" style={{ color: "var(--lux-gold)" }} />
            </div>
            <div>
              <LuxPill className="mb-3">Legal</LuxPill>
              <h1 className="text-4xl font-extrabold text-[var(--lux-text)] mb-2" style={{ letterSpacing: "-0.02em", lineHeight: 1.1 }} data-testid="privacy-page-title">
                Privacy Policy
              </h1>
              <p className="text-[14px] mb-4" style={{ color: "var(--lux-text-3)" }}>
                Effective: {effectiveDate} · Last updated: {lastUpdated}
              </p>
              <p className="text-[15px] leading-[1.7] max-w-2xl" style={{ color: "var(--lux-text-2)" }}>
                Fieldz AI (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) operates the Fieldz AI mobile application and web platform (the &quot;Service&quot;). This policy describes how we collect, use, share, and protect your information when you use our Service on any device.
              </p>
            </div>
          </div>
        </div>

        {/* At a Glance card */}
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8 mb-10 relative z-10">
          <div
            className="lux-card lux-card-glass max-w-[800px]"
            style={{ borderColor: "rgba(201, 168, 76, 0.30)" }}
            data-testid="privacy-summary"
          >
            <h2 className="text-[14px] font-semibold uppercase tracking-[0.12em] mb-4" style={{ color: "var(--lux-gold)" }}>
              At a Glance
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[14px]">
              {[
                [Eye, "We collect only what's needed to run the Service"],
                [Lock, "Your data is encrypted in transit and at rest"],
                [Trash2, "You can delete your account and data anytime"],
                [Globe, "We do not sell your personal information"],
              ].map(([Icon, txt], i) => (
                <div key={i} className="flex items-start gap-2">
                  <Icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--lux-gold)" }} />
                  <span style={{ color: "var(--lux-text-2)" }}>{txt}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Body — sticky TOC + content */}
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8 pb-20 grid lg:grid-cols-[220px_1fr] gap-10">
          <aside className="hidden lg:block">
            <nav className="lux-toc" aria-label="Table of contents">
              <h2 className="lux-toc-title">On this page</h2>
              <ul className="lux-toc-list">
                {SECTIONS.map((s) => (
                  <li key={s.id}>
                    <a href={`#${s.id}`} className={active === s.id ? "is-active" : ""} data-testid={`privacy-toc-${s.id}`}>
                      {s.n}. {s.title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          <main className="lux-prose">
            <section id="data-collected" className="scroll-mt-28">
              <h2>1. Information We Collect</h2>
              <h3>1.1 Information You Provide</h3>
              <ul>
                <li><strong>Account information:</strong> Name, email address, phone number, business name, address</li>
                <li><strong>Customer data:</strong> Contact details, job history, invoices, and notes you enter about your customers</li>
                <li><strong>Payment information:</strong> Billing details processed securely through Stripe (we do not store full card numbers)</li>
                <li><strong>Communications:</strong> SMS messages, call logs, and support requests</li>
                <li><strong>Photos:</strong> Images you capture using the in-app camera for job documentation</li>
                <li><strong>Team data:</strong> Technician names, emails, phone numbers, and assigned jobs</li>
              </ul>
              <h3>1.2 Information Collected Automatically</h3>
              <ul>
                <li><strong>Device information:</strong> Device model, operating system, browser type, unique device identifiers</li>
                <li><strong>Location data:</strong> Precise GPS location (only when you grant permission) for job routing, driving directions, and — for technicians who turn on location sharing — sharing their position with their company's dispatchers. Background location is collected only while a technician is on duty with location sharing enabled, and stops when sharing is turned off or the technician logs out</li>
                <li><strong>Usage data:</strong> Pages visited, features used, timestamps, crash reports</li>
                <li><strong>Network information:</strong> IP address, internet connectivity status</li>
                <li><strong>Push notification tokens:</strong> Device tokens for delivering notifications</li>
              </ul>
              <h3>1.3 Information from Third Parties</h3>
              <ul>
                <li><strong>Google Authentication:</strong> Name, email, and profile picture when you sign in with Google</li>
                <li><strong>Stripe:</strong> Payment confirmation and subscription status</li>
                <li><strong>QuickBooks:</strong> Accounting data when you connect your QuickBooks account</li>
              </ul>
            </section>

            <section id="data-safety" className="scroll-mt-28">
              <h2>2. Data Safety Summary</h2>
              <p>The following table summarizes what data we collect, why, and whether it is shared — formatted to align with Apple App Store and Google Play data safety requirements.</p>
              <div className="overflow-x-auto mt-4">
                <table
                  style={{
                    width: "100%",
                    minWidth: 600,
                    fontSize: 14,
                    borderCollapse: "separate",
                    borderSpacing: 0,
                    border: "1px solid var(--lux-border)",
                    borderRadius: 12,
                    overflow: "hidden",
                  }}
                >
                  <thead>
                    <tr style={{ background: "rgba(255,255,255,0.025)" }}>
                      {["Data Type", "Collected", "Purpose", "Shared"].map((h) => (
                        <th key={h} style={{ textAlign: "left", padding: "10px 14px", fontSize: 11, color: "var(--lux-text-3)", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600, borderBottom: "1px solid var(--lux-border)" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody style={{ color: "var(--lux-text-2)" }}>
                    <style>{`.lux-prose tbody td{padding:10px 14px;border-bottom:1px solid rgba(255,255,255,0.04);font-size:13px;} .lux-prose tbody tr:last-child td{border-bottom:none;} .lux-prose tbody td:first-child{color:var(--lux-text);font-weight:500;}`}</style>
                    <DataRow type="Name & Email" collected="Yes" purpose="Account, communication" shared="Auth providers" />
                    <DataRow type="Phone Number" collected="Yes" purpose="SMS, calls, 2FA" shared="Twilio" />
                    <DataRow type="Business Address" collected="Yes" purpose="Service area, invoices" shared="Google Maps" />
                    <DataRow type="Payment Info" collected="Via Stripe" purpose="Billing, subscriptions" shared="Stripe" />
                    <DataRow type="Precise Location" collected="With permission" purpose="Job routing, on-duty technician sharing (incl. background)" shared="Within your company only" />
                    <DataRow type="Camera / Photos" collected="With permission" purpose="Job documentation" shared="Not shared" />
                    <DataRow type="Push Token" collected="With permission" purpose="Notifications" shared="Expo / OneSignal" />
                    <DataRow type="Device Info" collected="Automatically" purpose="Compatibility, debugging" shared="Analytics" />
                    <DataRow type="Usage / Analytics" collected="Automatically" purpose="Improve service" shared="PostHog (anonymized)" />
                    <DataRow type="SMS Content" collected="Yes" purpose="Customer messaging" shared="Twilio" />
                    <DataRow type="Call Recordings" collected="If enabled" purpose="Quality assurance" shared="Twilio" />
                    <DataRow type="Customer Data" collected="User-entered" purpose="CRM functionality" shared="Not shared" />
                  </tbody>
                </table>
              </div>
              <p>We do <strong>not</strong> sell any personal data to third parties. We do <strong>not</strong> use personal data for advertising.</p>
            </section>

            <section id="permissions" className="scroll-mt-28">
              <h2>3. Device Permissions (Mobile App)</h2>
              <p>The Fieldz AI mobile app may request the following device permissions. Each is optional and only activated when you grant access:</p>
              <div className="space-y-3 mt-4">
                <PermRow icon={Camera} title="Camera" body="Used to take photos for job documentation. Photos are stored on our servers and linked to the relevant job. You can delete photos at any time." />
                <PermRow icon={MapPin} title="Location (While Using the App)" body="Used to display your jobs on a map, calculate driving routes between appointments, and show your position. Can be revoked in your device settings at any time — the scheduled-job map still works without it." />
                <PermRow icon={MapPin} title="Background Location (Technicians, Optional)" body="If you are a technician and turn ON the in-app 'Share location' toggle, the app may continue sending your location to your company's dispatchers while it is in the background, so they can see who is nearby and coordinate jobs. This is always opt-in: it only runs while sharing is enabled, is clearly indicated (a visible in-app indicator, plus the standard Android foreground-service notification / iOS location indicator), and stops immediately when you turn sharing off, go off duty, or log out. Your location is visible only to authorized members of your own company (owners, admins, dispatchers) and is never sold or used for advertising. Timestamps are always shown with your position so outdated locations are never presented as live." />
                <PermRow icon={Bell} title="Push Notifications" body="Used to send job updates, appointment reminders, new messages, and important account alerts. You can manage notification preferences in Settings or disable them in your device settings." />
                <PermRow icon={Phone} title="Phone / SMS" body="The app may open your device's phone or SMS app for direct communication. We do not read your personal messages or call history." />
              </div>
            </section>

            <section id="usage" className="scroll-mt-28">
              <h2>4. How We Use Your Information</h2>
              <ul>
                <li><strong>Provide the Service:</strong> Manage jobs, customers, invoices, team, and communications</li>
                <li><strong>Maps & dispatching:</strong> Geocode job addresses, draw routes between scheduled appointments, and show on-duty technicians' latest shared locations to authorized company members (owners, admins, dispatchers)</li>
                <li><strong>Process payments:</strong> Handle subscriptions, account balance top-ups, and invoice payments via Stripe</li>
                <li><strong>Send communications:</strong> Deliver SMS notifications, appointment reminders, and job updates via Twilio</li>
                <li><strong>AI features:</strong> Power automation workflows and AI-assisted responses using OpenAI (no personal data is used to train AI models)</li>
                <li><strong>Push notifications:</strong> Send timely alerts about jobs, messages, and account activity</li>
                <li><strong>Improve the Service:</strong> Analyze usage patterns, fix bugs, and develop new features</li>
                <li><strong>Security:</strong> Detect fraud, prevent abuse, and protect user accounts</li>
                <li><strong>Legal compliance:</strong> Meet regulatory obligations and respond to legal requests</li>
              </ul>
            </section>

            <section id="third-parties" className="scroll-mt-28">
              <h2>5. Third-Party Services & Data Sharing</h2>
              <p>We share data with the following third-party service providers only as necessary to operate the Service:</p>
              <div className="space-y-3 mt-4">
                <ThirdPartyService name="Stripe" purpose="Payment processing and subscription management" data="Email, payment method, transaction amounts" url="https://stripe.com/privacy" />
                <ThirdPartyService name="Twilio" purpose="SMS messaging, voice calls, and phone number management" data="Phone numbers, message content, call metadata" url="https://www.twilio.com/legal/privacy" />
                <ThirdPartyService name="Google" purpose="Authentication (Sign in with Google), Maps display, address geocoding, and driving routes" data="Email, name, profile picture, address queries, job addresses and route waypoints" url="https://policies.google.com/privacy" />
                <ThirdPartyService name="OneSignal" purpose="Push notification delivery" data="Device tokens, notification content" url="https://onesignal.com/privacy_policy" />
                <ThirdPartyService name="OpenAI" purpose="AI-powered automation and text generation" data="Job context (anonymized), template content" url="https://openai.com/privacy" />
                <ThirdPartyService name="MailerSend" purpose="Transactional email delivery" data="Email addresses, email content" url="https://www.mailersend.com/legal/privacy-policy" />
                <ThirdPartyService name="Intuit QuickBooks" purpose="Accounting and bookkeeping sync" data="Invoices, customer names, amounts (when connected)" url="https://www.intuit.com/privacy/" />
                <ThirdPartyService name="PostHog" purpose="Product analytics (anonymized)" data="Page views, feature usage, device type" url="https://posthog.com/privacy" />
              </div>
              <p className="mt-4">We do not sell, rent, or trade your personal information to any third party for marketing purposes.</p>
            </section>

            <section id="security" className="scroll-mt-28">
              <h2>6. Data Security</h2>
              <ul>
                <li><strong>Encryption:</strong> All data is encrypted in transit (TLS 1.2+) and at rest</li>
                <li><strong>Authentication:</strong> Passwords are hashed using bcrypt; JWT tokens for session management</li>
                <li><strong>Secure storage:</strong> Sensitive credentials (tokens, API keys) stored in encrypted secure storage on mobile devices</li>
                <li><strong>Access controls:</strong> Role-based permissions for team members and technicians</li>
                <li><strong>Payment security:</strong> Payment processing handled entirely by Stripe (PCI DSS Level 1 certified); we never store full card numbers</li>
                <li><strong>Infrastructure:</strong> Hosted on secure cloud infrastructure with regular security updates</li>
              </ul>
              <p>While we implement industry-standard protections, no system is 100% secure. We encourage you to use strong passwords and enable available security features.</p>
            </section>

            <section id="retention" className="scroll-mt-28">
              <h2>7. Data Retention & Deletion</h2>
              <h3>Retention</h3>
              <ul>
                <li>Account data is retained for as long as your account is active</li>
                <li>Usage logs and analytics data are retained for up to 24 months</li>
                <li>Payment records are retained as required by tax and financial regulations</li>
                <li>SMS and call logs are retained for 12 months unless you delete them earlier</li>
                <li>Technician location data: only the most recent shared position is stored (each update replaces the previous one — no movement history is kept). It is removed when the account is deleted and stops updating the moment sharing is turned off</li>
              </ul>
              <h3>Account Deletion</h3>
              <p>You may request deletion of your account and associated data at any time by:</p>
              <ul>
                <li>Going to <strong>Settings &gt; Account &gt; Delete Account</strong> in the app</li>
                <li>Emailing <a href="mailto:privacy@fieldzai.com">privacy@fieldzai.com</a> with your account email</li>
              </ul>
              <p>Upon receiving a deletion request, we will permanently delete your personal data within <strong>30 days</strong>, except where retention is required by law. Customer data you entered (jobs, invoices, etc.) will also be deleted. This action is irreversible.</p>
            </section>

            <section id="rights" className="scroll-mt-28">
              <h2>8. Your Privacy Rights</h2>
              <h3>For All Users</h3>
              <ul>
                <li><strong>Access:</strong> Request a copy of all personal data we hold about you</li>
                <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                <li><strong>Deletion:</strong> Request permanent deletion of your account and data</li>
                <li><strong>Export:</strong> Download your data in a portable format</li>
                <li><strong>Opt-out:</strong> Unsubscribe from marketing emails; disable push notifications</li>
                <li><strong>Withdraw consent:</strong> Revoke any previously granted permission at any time through your device settings</li>
              </ul>
              <h3>California Residents (CCPA / CPRA)</h3>
              <p>If you are a California resident, you have additional rights under the California Consumer Privacy Act:</p>
              <ul>
                <li>Right to know what personal information is collected, used, and shared</li>
                <li>Right to delete personal information</li>
                <li>Right to opt out of the sale of personal information (we do not sell your data)</li>
                <li>Right to non-discrimination for exercising your privacy rights</li>
                <li>Right to correct inaccurate personal information</li>
              </ul>
              <h3>European Users (GDPR)</h3>
              <p>If you are in the European Economic Area, you have rights under the General Data Protection Regulation:</p>
              <ul>
                <li>Right to access, rectify, erase, and port your data</li>
                <li>Right to restrict or object to processing</li>
                <li>Right to withdraw consent at any time</li>
                <li>Right to lodge a complaint with a supervisory authority</li>
              </ul>
              <p>Our legal bases for processing: contract performance, legitimate interest, consent, and legal obligation.</p>
              <p>To exercise any of these rights, contact <a href="mailto:privacy@fieldzai.com">privacy@fieldzai.com</a>. We will respond within 30 days.</p>
            </section>

            <section id="transfers" className="scroll-mt-28">
              <h2>9. International Data Transfers</h2>
              <p>Your information may be transferred to and processed in countries other than your own, including the United States, where our servers and third-party service providers are located. These countries may have different data protection laws.</p>
              <p>We ensure appropriate safeguards are in place for international transfers, including Standard Contractual Clauses (SCCs) where required by GDPR, and ensuring third-party providers maintain adequate security standards.</p>
            </section>

            <section id="children" className="scroll-mt-28">
              <h2>10. Children&apos;s Privacy</h2>
              <p>The Service is not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that a child under 18 has provided us with personal information, we will take steps to delete it promptly.</p>
              <p>If you are a parent or guardian and believe your child has provided us with personal information, please contact us at <a href="mailto:privacy@fieldzai.com">privacy@fieldzai.com</a>.</p>
            </section>

            <section id="cookies" className="scroll-mt-28">
              <h2>11. Cookies & Tracking Technologies</h2>
              <p>Our web platform uses:</p>
              <ul>
                <li><strong>Essential cookies:</strong> Required for authentication and core functionality (session tokens)</li>
                <li><strong>Analytics:</strong> PostHog for anonymized product usage analytics</li>
                <li><strong>Local storage:</strong> To persist your login session and preferences</li>
              </ul>
              <p>Our mobile app does not use cookies. It uses secure on-device storage (Expo SecureStore) for authentication tokens.</p>
              <p>We do not use third-party advertising cookies or cross-app tracking. We respect &quot;Do Not Track&quot; browser signals.</p>
            </section>

            <section id="changes" className="scroll-mt-28">
              <h2>12. Changes to This Policy</h2>
              <p>We may update this Privacy Policy from time to time. When we make material changes, we will:</p>
              <ul>
                <li>Update the &quot;Last updated&quot; date at the top of this page</li>
                <li>Send a notification via email or in-app alert for significant changes</li>
                <li>Post the updated policy on our website and within the app</li>
              </ul>
              <p>Your continued use of the Service after changes take effect constitutes acceptance of the revised policy. We encourage you to review this page periodically.</p>
            </section>

            <section id="contact" className="scroll-mt-28 mt-10">
              <div
                className="lux-card lux-card-glass"
                style={{ borderColor: "rgba(201, 168, 76, 0.30)", boxShadow: "0 0 32px rgba(201, 168, 76, 0.10)" }}
                data-testid="privacy-contact"
              >
                <h2 style={{ marginTop: 0 }}>13. Contact Us</h2>
                <p>If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, contact us:</p>
                <div className="mt-3 space-y-2">
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 shrink-0" style={{ color: "var(--lux-gold)" }} />
                    <a href="mailto:privacy@fieldzai.com">privacy@fieldzai.com</a>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--lux-text-3)" }} />
                    <p style={{ marginBottom: 0 }}>Fieldz AI<br />Attn: Privacy Team</p>
                  </div>
                </div>
                <p className="mt-3">For data deletion requests, please include your registered email address. We aim to respond within 30 days.</p>
              </div>
            </section>
          </main>
        </div>
      </div>

      <LandingFooter />
    </div>
  );
};

export default PrivacyPolicyPage;
